import { useEffect, useRef, useState, type FormEvent } from 'react';
import {
  demoAssetFieldLabels,
  demoAssetFixtures,
  demoAssetLabels,
  emptyDemoAsset,
  proposeDemoAssetVersion,
  saveDemoAsset,
  submitDemoAsset,
  validateDemoAsset,
  type DemoAssetDraft,
  type DemoAssetFields,
  type DemoAssetStatus,
} from '../fixtures/asset-data-management-demo';
import './asset-data-management-prototype.css';

export function DemoAssetSummary({ fields }: { readonly fields: DemoAssetFields }) {
  return (
    <dl className="asset-ux__summary">
      {(Object.keys(demoAssetFieldLabels) as (keyof DemoAssetFields)[]).map((key) => (
        <div key={key}>
          <dt>{demoAssetFieldLabels[key]}</dt>
          <dd>{fields[key] || '尚未填写'}</dd>
        </div>
      ))}
    </dl>
  );
}
export function DemoAssetVersions({ row }: { readonly row: DemoAssetDraft }) {
  return (
    <section className="asset-ux__versions" aria-label="版本差异">
      <h3>演示版本比较 · v1 只读 / 拟议 v{row.version}</h3>
      {row.publishedV1 ? (
        <div className="asset-ux__comparison">
          {(Object.keys(demoAssetFieldLabels) as (keyof DemoAssetFields)[]).map((key) => (
            <section
              key={key}
              className={row.publishedV1?.[key] !== row.fields[key] ? 'asset-ux__changed' : ''}
            >
              <h4>
                {demoAssetFieldLabels[key]}{' '}
                {row.publishedV1?.[key] !== row.fields[key] ? '· 已变更' : '· 无变化'}
              </h4>
              <div>
                <p>
                  <b>只读 v1</b>
                  {row.publishedV1?.[key] || '未填写'}
                </p>
                <p>
                  <b>拟议 v{row.version}</b>
                  {row.fields[key] || '未填写'}
                </p>
              </div>
            </section>
          ))}
        </div>
      ) : (
        <p>新草稿没有已发布演示 v1；版本基线未知。</p>
      )}
    </section>
  );
}
export function AssetDataManagementPrototype({
  enabled = import.meta.env.DEV,
}: {
  readonly enabled?: boolean;
}) {
  const [rows, setRows] = useState<readonly DemoAssetDraft[]>(demoAssetFixtures);
  const [view, setView] = useState<'form' | 'list' | 'detail' | null>(null);
  const [fields, setFields] = useState<DemoAssetFields>(emptyDemoAsset);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [filter, setFilter] = useState<DemoAssetStatus | ''>('');
  const [errors, setErrors] = useState<Partial<Record<keyof DemoAssetFields, string>>>({});
  const [message, setMessage] = useState('');
  const dialog = useRef<HTMLDialogElement>(null);
  const closeButton = useRef<HTMLButtonElement>(null);
  const opener = useRef<HTMLElement | null>(null);
  const listOpenerId = useRef<string | null>(null);
  const listScroll = useRef(0);
  const counter = useRef(0);
  const open = view !== null;
  useEffect(() => {
    if (!open) return;
    const element = dialog.current;
    const capturedOpener = opener.current;
    const scroll = window.scrollY;
    if (element && !element.open) element.showModal();
    closeButton.current?.focus();
    return () => {
      if (element?.open) element.close();
      if (capturedOpener?.isConnected) capturedOpener.focus({ preventScroll: true });
      window.scrollTo(0, scroll);
    };
  }, [open]);
  if (!enabled) return null;
  const selected = rows.find((row) => row.demoAssetDraftId === selectedId);
  function openFrom(button: HTMLButtonElement, next: 'form' | 'list') {
    opener.current = button;
    setView(next);
    setMessage('');
    setErrors({});
    if (next === 'form') {
      setFields(emptyDemoAsset);
      setEditingId(null);
    }
  }
  function save(event?: FormEvent<HTMLFormElement>, submit = false) {
    event?.preventDefault();
    const validation = validateDemoAsset(fields, rows, editingId);
    setErrors(validation);
    if (Object.keys(validation).length) {
      setMessage('请检查标记字段，草稿未保存。');
      return;
    }
    const id = editingId ?? `demoAssetDraftId-session-${++counter.current}`;
    const next = saveDemoAsset(rows, fields, id, editingId);
    setRows(submit ? submitDemoAsset(next, id) : next);
    setSelectedId(id);
    setView('detail');
    setMessage(
      submit
        ? '已在本端模拟待审核；未发送到08管理端。'
        : '本次会话草稿已记录在 React 内存，未保存到服务器。',
    );
    dialog.current?.scrollTo(0, 0);
    closeButton.current?.focus();
  }
  function edit(row: DemoAssetDraft) {
    setEditingId(row.demoAssetDraftId);
    setFields({ ...row.fields });
    setErrors({});
    setView('form');
    setMessage('');
    dialog.current?.scrollTo(0, 0);
    closeButton.current?.focus();
  }
  function returnList() {
    setView('list');
    setMessage('');
    requestAnimationFrame(() => {
      dialog.current?.scrollTo(0, listScroll.current);
      const restoredOpener = Array.from(
        dialog.current?.querySelectorAll<HTMLButtonElement>('[data-demo-asset-row]') ?? [],
      ).find((button) => button.dataset.demoAssetRow === listOpenerId.current);
      if (restoredOpener) restoredOpener.focus({ preventScroll: true });
      else closeButton.current?.focus();
    });
  }
  return (
    <section className="asset-ux" aria-label="05装备资料本地演示工具">
      <div className="asset-ux__tools">
        <div>
          <strong>装备资料工作流 · DEV 原型</strong>
          <p>本端独立演示 · 仅会话内存 · 刷新新增项消失</p>
        </div>
        <button type="button" onClick={(event) => openFrom(event.currentTarget, 'form')}>
          登记装备（演示）
        </button>
        <button type="button" onClick={(event) => openFrom(event.currentTarget, 'list')}>
          我的提交（演示）
        </button>
      </div>
      <dialog
        ref={dialog}
        className="asset-ux__dialog"
        aria-labelledby="asset-ux-title"
        onCancel={(event) => {
          event.preventDefault();
          setView(null);
        }}
      >
        <header className="asset-ux__header">
          <div>
            <span>05 FIRE SERVICE · DEMO_ONLY</span>
            <h2 id="asset-ux-title">
              {view === 'form'
                ? editingId
                  ? '修订装备资料 · 拟议版本'
                  : '新增装备资料 · 本地流程演示'
                : view === 'list'
                  ? '我的提交（演示）'
                  : '装备提交详情（演示）'}
            </h2>
          </div>
          <button ref={closeButton} type="button" onClick={() => setView(null)}>
            返回真实装备目录
          </button>
        </header>
        <p className="asset-ux__notice">
          无后端保存 · 身份/机构授权尚未接入 · 不会正式提交 · 不同步05/06/08
        </p>
        <p role="status" className="asset-ux__feedback">
          {message}
        </p>
        {view === 'form' && (
          <div className="asset-ux__layout">
            <form noValidate onSubmit={(event) => save(event)}>
              <fieldset>
                <legend>基本资料与声明</legend>
                {(Object.keys(demoAssetFieldLabels) as (keyof DemoAssetFields)[]).map((key) => (
                  <label key={key} htmlFor={`asset-ux-${key}`}>
                    {demoAssetFieldLabels[key]}
                    {key === 'configuration' || key === 'source' ? (
                      <textarea
                        id={`asset-ux-${key}`}
                        value={fields[key]}
                        aria-invalid={!!errors[key]}
                        aria-describedby={errors[key] ? `asset-ux-error-${key}` : undefined}
                        onChange={(event) => setFields({ ...fields, [key]: event.target.value })}
                      />
                    ) : (
                      <input
                        id={`asset-ux-${key}`}
                        value={fields[key]}
                        aria-invalid={!!errors[key]}
                        aria-describedby={errors[key] ? `asset-ux-error-${key}` : undefined}
                        onChange={(event) => setFields({ ...fields, [key]: event.target.value })}
                      />
                    )}
                    {errors[key] && (
                      <span id={`asset-ux-error-${key}`} className="asset-ux__error">
                        {errors[key]}
                      </span>
                    )}
                    <small>
                      {key === 'configuration'
                        ? '最多1200字符；用途声明不构成已验证能力。'
                        : key === 'source'
                          ? '最多600字符；注明来源与使用限制。'
                          : key === 'name'
                            ? '最多80字符，必填。'
                            : key === 'category'
                              ? '最多40字符，必填。'
                              : '最多40字符，仅检验本端演示编号。'}
                    </small>
                  </label>
                ))}
              </fieldset>
              <fieldset>
                <legend>关联与资料</legend>
                <p>
                  Product Model Reference：<b>UNKNOWN / NOT LINKED</b>
                </p>
                <p>Provider：未关联 / UNKNOWN。暂无经核对的精确 ID/version 来源。</p>
                <p>
                  本装备自身能力：<b>UNKNOWN / NOT ESTABLISHED</b>
                </p>
                <div className="asset-ux__media">
                  图片与资料 · 待上传占位（演示）
                  <br />
                  无正式上传，也不宣称实物照片或资料权属。
                </div>
              </fieldset>
              <div className="asset-ux__actions">
                <button type="submit">保存本次会话草稿（演示）</button>
                <button type="button" onClick={() => save(undefined, true)}>
                  模拟提交审核（未发送管理端）
                </button>
                <button type="button" onClick={returnList}>
                  放弃编辑
                </button>
              </div>
            </form>
            <aside>
              <h3>当前填写摘要</h3>
              <DemoAssetSummary fields={fields} />
              <p>机构范围：UNKNOWN。此原型不签发 LocalAsset 身份，不提供登记或行动授权。</p>
              <p>资料发布 ≠ 能力验证 ≠ 在线/READY ≠ 调度授权。</p>
              {editingId && selected?.publishedV1 && (
                <p>只读 v1 保持原样；此表单仅编辑拟议 v{selected.version}。</p>
              )}
            </aside>
          </div>
        )}
        {view === 'list' && (
          <>
            <div className="asset-ux__list-heading">
              <label>
                演示状态筛选
                <select
                  value={filter}
                  onChange={(event) => setFilter(event.target.value as DemoAssetStatus | '')}
                >
                  <option value="">全部演示状态</option>
                  {(Object.keys(demoAssetLabels) as DemoAssetStatus[]).map((status) => (
                    <option key={status} value={status}>
                      {demoAssetLabels[status]}
                    </option>
                  ))}
                </select>
              </label>
              <span>
                本次会话 {rows.filter((row) => row.origin === 'SESSION_ONLY').length} 条 · 静态样例{' '}
                {rows.filter((row) => row.origin === 'STATIC_DEMO').length} 条
              </span>
            </div>
            <ul className="asset-ux__rows">
              {rows
                .filter((row) => !filter || row.status === filter)
                .map((row) => (
                  <li key={row.demoAssetDraftId}>
                    <div>
                      <strong>{row.fields.name}</strong>
                      <p>
                        {demoAssetLabels[row.status]} · v{row.version} ·{' '}
                        {row.origin === 'STATIC_DEMO' ? '本端固定样例' : '本次会话'}
                      </p>
                      <code>{row.demoAssetDraftId}</code>
                    </div>
                    <button
                      type="button"
                      data-demo-asset-row={row.demoAssetDraftId}
                      onClick={() => {
                        listOpenerId.current = row.demoAssetDraftId;
                        listScroll.current = dialog.current?.scrollTop ?? 0;
                        setSelectedId(row.demoAssetDraftId);
                        setView('detail');
                        setMessage('');
                        dialog.current?.scrollTo(0, 0);
                        closeButton.current?.focus();
                      }}
                    >
                      查看资料与版本
                    </button>
                  </li>
                ))}
            </ul>
            {!rows.some((row) => !filter || row.status === filter) && <p>当前演示状态没有记录。</p>}
          </>
        )}
        {view === 'detail' && selected && (
          <>
            <div className="asset-ux__detail-heading">
              <div>
                <h3>{selected.fields.name}</h3>
                <p>
                  {demoAssetLabels[selected.status]} · DEMO_ONLY · v{selected.version}
                </p>
                <code>{selected.demoAssetDraftId}</code>
              </div>
              <button type="button" onClick={returnList}>
                返回我的提交
              </button>
            </div>
            <p className="asset-ux__notice">
              {selected.feedback || '本端会话草稿，未发送管理端。'}
            </p>
            <DemoAssetSummary fields={selected.fields} />
            <p>
              产品型号：UNKNOWN / NOT LINKED · Provider：未关联 · 机构权限：UNKNOWN · 能力：UNKNOWN
              / NOT ESTABLISHED
            </p>
            <DemoAssetVersions row={selected} />
            <div className="asset-ux__actions">
              {(selected.status === 'DRAFT' || selected.status === 'CHANGES_REQUESTED') && (
                <button type="button" onClick={() => edit(selected)}>
                  修订拟议 v{selected.version}（演示）
                </button>
              )}
              {selected.status === 'DRAFT' && (
                <button
                  type="button"
                  onClick={() => {
                    setRows(submitDemoAsset(rows, selected.demoAssetDraftId));
                    setMessage('已在本端模拟待审核；未发送到08管理端。');
                  }}
                >
                  模拟提交审核（未发送管理端）
                </button>
              )}
              {selected.status === 'PUBLISHED' && (
                <button
                  type="button"
                  onClick={() => {
                    const next = proposeDemoAssetVersion(rows, selected.demoAssetDraftId);
                    setRows(next);
                    const proposed = next.find(
                      (row) => row.demoAssetDraftId === `${selected.demoAssetDraftId}-proposed-v2`,
                    );
                    if (proposed?.status === 'DRAFT') {
                      setSelectedId(proposed.demoAssetDraftId);
                      edit(proposed);
                    } else setMessage('拟议 v2 已模拟提交；不能覆盖待审核版本。');
                  }}
                >
                  建立独立拟议 v2（演示）
                </button>
              )}
            </div>
            {selected.status === 'PENDING_REVIEW' && (
              <p>未送达08；无真实审核意见或 Owner 发布结果，本端不可批准发布。</p>
            )}
          </>
        )}
      </dialog>
    </section>
  );
}
